import { Text, View } from "@react-pdf/renderer";
import { stripNbsp } from "@src/utils";
import {
  createLocalMarked,
  markFormattingHtml,
  splitStoredMarkdown,
} from "@src/utils/markedConfig";

const localMarked = createLocalMarked();

const FONT_SIZE_BASE = 8;

const headingStyles = {
  1: {
    fontSize: 14,
    fontFamily: "Inter-Bold",
    fontWeight: 700,
    marginBottom: 4,
    marginTop: 6,
  },
  2: {
    fontSize: 12,
    fontFamily: "Inter-Bold",
    fontWeight: 700,
    marginBottom: 3,
    marginTop: 5,
  },
  3: {
    fontSize: 10,
    fontFamily: "Inter-Bold",
    fontWeight: 700,
    marginBottom: 2,
    marginTop: 4,
  },
};

const paragraphStyle = {
  fontSize: FONT_SIZE_BASE,
  fontFamily: "Inter",
  marginBottom: 0,
};

const tableRowStyle = { ...paragraphStyle, marginBottom: 0 };

const listItemStyle = {
  flexDirection: "row",
  marginBottom: 1,
};

const listTextBase = {
  fontSize: FONT_SIZE_BASE,
  fontFamily: "Inter",
};
const listBulletStyle = { ...listTextBase, width: 10 };
const listNumberStyle = { ...listTextBase, width: 14 };
const listContentStyle = { ...listTextBase, flex: 1 };

const boldStyle = {
  fontFamily: "Inter-Bold",
  fontWeight: 700,
};

const underlineStyle = {
  textDecoration: "underline",
};

// Inline token types that map directly to a style
const INLINE_STYLES = {
  strong: boldStyle,
  underline: underlineStyle,
  // No Inter-Italic font is registered, so em only renders its children.
  em: {},
};

function renderInlineTokens(tokens, keyPrefix = "") {
  let bold = false;
  return tokens.map((token, i) => {
    const key = `${keyPrefix}${i}`;
    if (token.formatting) {
      if (token.text === "<strong>") bold = true;
      if (token.text === "</strong>") bold = false;
      return token.text === "<br>" ? (
        <Text key={key}>{"\n"}</Text>
      ) : null;
    }
    const ownStyle = INLINE_STYLES[token.type];
    const style = bold ? { ...boldStyle, ...ownStyle } : ownStyle;
    if (style) {
      return (
        <Text key={key} style={style}>
          {token.tokens
            ? renderInlineTokens(token.tokens, `${key}-`)
            : (token.text ?? token.raw ?? "")}
        </Text>
      );
    }
    // text, codespan, escape, etc. — render as plain text
    return <Text key={key}>{token.text ?? token.raw ?? ""}</Text>;
  });
}

function renderBlockToken(token, index, compact = false) {
  const key = `block-${index}`;
  const textStyle = compact
    ? { ...paragraphStyle, marginBottom: 0 }
    : paragraphStyle;

  if (token.type === "heading") {
    const style = headingStyles[token.depth] ?? headingStyles[3];
    return (
      <Text key={key} style={style}>
        {token.tokens
          ? renderInlineTokens(token.tokens, `${key}-`)
          : (token.text ?? token.raw ?? "")}
      </Text>
    );
  }

  if (token.type === "list") {
    return renderList(token, key, compact);
  }

  if (token.type === "blockquote") {
    return (
      <View key={key} style={{ marginBottom: compact ? 0 : 2 }}>
        {(token.tokens ?? []).map((inner, j) =>
          renderBlockToken(inner, `${key}-bq-${j}`, compact),
        )}
      </View>
    );
  }

  if (token.type === "code") {
    return (
      <Text key={key} style={textStyle}>
        {token.text}
      </Text>
    );
  }

  if (token.type === "table") {
    return renderTable(token, key);
  }

  if (token.type === "paragraph") {
    return (
      <Text key={key} style={textStyle}>
        {token.tokens
          ? renderInlineTokens(token.tokens, `${key}-`)
          : (token.text ?? token.raw ?? "")}
      </Text>
    );
  }

  if (token.type === "space") {
    return compact ? null : <Text key={key}>{"\n"}</Text>;
  }

  if (token.type === "hr") {
    return (
      <View
        key={key}
        style={{
          borderBottomWidth: 0.5,
          borderBottomColor: "#cccccc",
          borderBottomStyle: "solid",
          marginTop: 4,
          marginBottom: 4,
        }}
      />
    );
  }

  // Fallback: render text content for any unhandled token type
  return (
    <Text key={key} style={textStyle}>
      {token.text ?? token.raw ?? ""}
    </Text>
  );
}

function renderList(token, key, compact = false) {
  return (
    <View key={key} style={{ marginBottom: compact ? 0 : 2 }}>
      {token.items.map((item, i) => {
        const { inline, nested } = splitListItemTokens(item.tokens);
        return (
          <View key={`${key}-li-${i}`}>
            <View style={listItemStyle}>
              <Text
                style={
                  token.ordered ? listNumberStyle : listBulletStyle
                }
              >
                {token.ordered
                  ? `${(token.start ?? 1) + i}.`
                  : "\u2022"}
              </Text>
              <Text style={listContentStyle}>
                {renderInlineTokens(inline, `${key}-li-${i}-`)}
              </Text>
            </View>
            {nested.length > 0 && (
              <View style={{ paddingLeft: 10 }}>
                {nested.map((nestedToken, j) =>
                  renderBlockToken(
                    nestedToken,
                    `${key}-li-${i}-n-${j}`,
                    compact,
                  ),
                )}
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
}

function renderPipeRow(cells, keyPrefix) {
  const parts = [];
  cells.forEach((cell, j) => {
    parts.push(
      <Text key={`${keyPrefix}${j}-sep`}>
        {j === 0 ? "| " : " | "}
      </Text>,
    );
    const content = cell.tokens
      ? renderInlineTokens(cell.tokens, `${keyPrefix}${j}-`)
      : [<Text key={`${keyPrefix}${j}-t`}>{cell.text}</Text>];
    parts.push(...content);
  });
  parts.push(<Text key={`${keyPrefix}end`}> |</Text>);
  return parts;
}

function renderTable(token, key) {
  return (
    <View key={key} style={{ marginBottom: 2 }}>
      <Text key={`${key}-header`} style={tableRowStyle}>
        {renderPipeRow(token.header, `${key}-h-`)}
      </Text>
      {token.rows.map((row, i) => (
        <Text key={`${key}-row-${i}`} style={tableRowStyle}>
          {renderPipeRow(row, `${key}-r-${i}-`)}
        </Text>
      ))}
    </View>
  );
}

/**
 * Splits list item tokens into inline content vs nested block tokens (sub-lists).
 * Inline tokens are flattened out of any paragraph/text wrappers marked adds.
 */
function splitListItemTokens(tokens) {
  const inline = [];
  const nested = [];

  for (const token of tokens ?? []) {
    if (token.type === "list") {
      nested.push(token);
    } else if (
      (token.type === "text" || token.type === "paragraph") &&
      token.tokens
    ) {
      inline.push(...token.tokens);
    } else {
      inline.push(token);
    }
  }

  return { inline, nested };
}

/**
 * Parse a markdown string (stored with single \n between paragraphs)
 * into an array of marked tokens, matching the same pre-processing
 * used by markdownToHtmlPreservingBlanks in richTextFormatUtils.ts.
 */
export function parseMarkdown(text) {
  if (!text) return [];

  return splitStoredMarkdown(stripNbsp(text)).flatMap((part) => {
    if (typeof part === "number") {
      return Array.from({ length: part }, () => ({
        type: "space",
        raw: "\n",
      }));
    }
    // Lexer space tokens come from expandNewlines' \n\n, not stored blank lines.
    const tokens = localMarked
      .lexer(part)
      .filter((t) => t.type !== "space");
    localMarked.walkTokens(tokens, markFormattingHtml);
    return tokens;
  });
}

/**
 * React-pdf component that renders a markdown string with basic formatting.
 *
 * Supports: bold, underline, italic, bullet lists, numbered lists,
 * headings (1-3), horizontal rules. Unknown syntax renders as plain text.
 */
export function MarkdownPdfText({ children, compact = false }) {
  if (!children || typeof children !== "string") return null;

  const tokens = parseMarkdown(children);
  return (
    <>
      {tokens.map((token, i) => renderBlockToken(token, i, compact))}
    </>
  );
}
